/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { useTheme } from '@fluentui/react/lib/Theme';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import * as React from 'react';
import { CustomResponseFields } from '../../..';
import { tacRequestSettingsVar } from '../../../../../apollo/reactiveVariables/TacRequestSettingsVariables';
import { RequestSettings } from '../../../../../service/gen';
import { RouteTypes } from '../../../../../service/gen/templates';
import { getTooltipHostStyles } from '../../../../../styles/custom/TooltipHostStyles';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { getHTMLDir, stringFormat } from '../../../../../utilities/Utils';
import { getDescriptionStyles, getTitleStyles } from '../../CreateTemplateInstanceWizard.styles';
import { TemplateInstanceApproverSettings } from './TemplateApproverSettings/TemplateApproverSettings';
import {
    TemplateInstanceApproverSettingsV1,
    TemplateInstanceApproverSettingsV1Props,
} from './TemplateApproverSettings/TemplateApproverSettingsV1';
import {
    TemplateInstanceApproverSettingsV2,
    TemplateInstanceApproverSettingsV2Props,
} from './TemplateApproverSettings/TemplateApproverSettingsV2';
import { getStackStyles } from './WorkflowSettings.styles';

interface InnerWorkflowSettingsComponentProps {
    workflowSchemaVersion: string;
    allMustRespond?: boolean;
    onAllMustRespondChange?: (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => void;
    isCustomResponsesEnabled: boolean;
    onCustomResponsesToggled?: (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => void;
    onCustomResponsesFieldsChange?: (responses: string[]) => void;
    customResponsesFields?: string[];
    maxCustomResponseOptions: number;
    requiredCustomResponseOptions: number;
    isWordCategoryTemplate: boolean;
    isFileAttachmentRequired: boolean;
    onIsFileAttachmentRequiredChange?: (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => void;
    showApproverCommentsRequired: boolean;
    isApproverCommentsRequired: boolean;
    onIsApproverCommentRequiredChange?: (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => void;
}

export type WorkflowSettingsComponentProps = InnerWorkflowSettingsComponentProps &
    (TemplateInstanceApproverSettingsV1Props | TemplateInstanceApproverSettingsV2Props);

export const WorkflowSettingsComponent: React.FC<WorkflowSettingsComponentProps> = (
    props: WorkflowSettingsComponentProps
): JSX.Element => {
    const host = getAppHost();
    const loc = host.getLocale();
    const theme = useTheme();

    const tacRequestSettings = useReactiveVar<RequestSettings>(tacRequestSettingsVar);
    const { TemplateSequentialApprovalsEnabled } = host.getEcsConfig().MicrosoftTeamApprovals;
    const {
        workflowSchemaVersion,
        isCustomResponsesEnabled,
        onCustomResponsesToggled,
        onCustomResponsesFieldsChange,
        customResponsesFields,
        maxCustomResponseOptions,
        requiredCustomResponseOptions,
        isWordCategoryTemplate,
        isFileAttachmentRequired,
        onIsFileAttachmentRequiredChange,
        showApproverCommentsRequired,
        isApproverCommentsRequired,
        onIsApproverCommentRequiredChange,
    } = props;
    const isAdminApproverCommentsRequired =
        showApproverCommentsRequired && tacRequestSettings && tacRequestSettings.CommentsRequired;
    const routesType = (props as TemplateInstanceApproverSettingsV2Props).approverRouteType;
    const isSequential = (routesType & RouteTypes.StepRoute) === RouteTypes.StepRoute;

    return (
        <Stack styles={getStackStyles(theme)}>
            <Label
                id='workflow-settings-label'
                aria-label={loc.strings.workflow_settings_wizard_step_label}
                styles={getTitleStyles(theme)}>
                {loc.strings.workflow_settings_wizard_step_label}
            </Label>
            <Label
                id='workflow-settings-desc'
                aria-label={loc.strings.workflow_settings_wizard_step_description}
                styles={getDescriptionStyles(theme)}>
                {loc.strings.workflow_settings_wizard_step_description}
            </Label>
            <Stack className='template-instance-fields'>
                <Label className='template-label'>{loc.strings.file_attachment_label}</Label>
                <Toggle
                    checked={isFileAttachmentRequired || isWordCategoryTemplate}
                    disabled={isWordCategoryTemplate}
                    label={loc.strings.file_attachment_required_text}
                    inlineLabel
                    ariaLabel={loc.strings.file_attachment_required_text}
                    onChange={onIsFileAttachmentRequiredChange}
                />
                <Stack.Item>
                    {workflowSchemaVersion === 'v1.0' && (
                        <TemplateInstanceApproverSettingsV1 {...(props as TemplateInstanceApproverSettingsV1Props)} />
                    )}
                    {workflowSchemaVersion === 'v2.0' &&
                        (TemplateSequentialApprovalsEnabled || isSequential ? (
                            <TemplateInstanceApproverSettings {...(props as TemplateInstanceApproverSettingsV2Props)} />
                        ) : (
                            <TemplateInstanceApproverSettingsV2
                                {...(props as TemplateInstanceApproverSettingsV2Props)}
                            />
                        ))}
                </Stack.Item>

                {!isSequential && (
                    <Stack.Item className='template-management-custom-response-fields'>
                        <Label>{loc.strings.custom_response_label}</Label>
                        <Stack horizontal tokens={{ childrenGap: '12px' }}>
                            <Toggle
                                label={loc.strings.custom_response_toggle_text}
                                inlineLabel
                                checked={isCustomResponsesEnabled}
                                onChange={onCustomResponsesToggled}
                                ariaLabel={stringFormat(loc.strings.custom_response_toggle_aria_label, {
                                    state: isCustomResponsesEnabled
                                        ? loc.strings.checked_aria_label
                                        : loc.strings.unchecked_aria_label,
                                })}
                            />
                            <TooltipHost
                                styles={getTooltipHostStyles(theme, { root: { marginBottom: '4px' } })}
                                content={loc.strings.custom_response_toggle_tip}
                                directionalHint={
                                    getHTMLDir(host.getLocaleName()) === 'rtl'
                                        ? DirectionalHint.leftCenter
                                        : DirectionalHint.rightCenter
                                }>
                                <FontIcon
                                    iconName='Info'
                                    tabIndex={0}
                                    aria-label={loc.strings.custom_response_toggle_tip}
                                />
                            </TooltipHost>
                        </Stack>
                        <CustomResponseFields
                            key={isCustomResponsesEnabled ? 'customResponse' : 'defaultCustomResponse'}
                            isDisabled={!isCustomResponsesEnabled}
                            maximumResponsesCount={maxCustomResponseOptions}
                            requiredResponsesCount={requiredCustomResponseOptions}
                            onChange={onCustomResponsesFieldsChange}
                            defaultFields={
                                !isCustomResponsesEnabled
                                    ? [loc.strings.approve, loc.strings.reject]
                                    : customResponsesFields
                            }
                        />
                    </Stack.Item>
                )}
                {showApproverCommentsRequired && (
                    <Stack.Item className='template-management-approver-comments-fields'>
                        <Label>{loc.strings.approver_comments_label}</Label>
                        <Stack horizontal tokens={{ childrenGap: '12px' }}>
                            <Toggle
                                checked={isAdminApproverCommentsRequired || isApproverCommentsRequired}
                                onChange={onIsApproverCommentRequiredChange}
                                ariaLabel={stringFormat(loc.strings.approver_comments_label, {
                                    state: isApproverCommentsRequired
                                        ? loc.strings.approver_comments_required_on_text
                                        : loc.strings.approver_comments_required_off_text,
                                })}
                                disabled={isAdminApproverCommentsRequired}
                                onText={loc.strings.approver_comments_required_on_text}
                                offText={loc.strings.approver_comments_required_off_text}
                            />
                            <TooltipHost
                                styles={getTooltipHostStyles(theme, { root: { marginBottom: '4px' } })}
                                content={loc.strings.approver_comments_toggle_tip}
                                directionalHint={
                                    getHTMLDir(host.getLocaleName()) === 'rtl'
                                        ? DirectionalHint.leftCenter
                                        : DirectionalHint.rightCenter
                                }>
                                <FontIcon
                                    iconName='Info'
                                    tabIndex={0}
                                    aria-label={loc.strings.approver_comments_toggle_tip}
                                />
                            </TooltipHost>
                        </Stack>
                    </Stack.Item>
                )}
            </Stack>
        </Stack>
    );
};
